import React, { FC, ReactNode, useLayoutEffect } from 'react';
import classnames from 'classnames';

import {
    BottomTransition,
    TopTransition,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/OpacityTransitionGroup/OpacityTransitionGroup';
import {
    Spinner,
    SpinnerSize,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/Spinner';
import { useDoubleClick } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Layout/hooks/useDoubleClick';
import { usePlayingState } from '@/scenarios/legacy-player/hooks/usePlayingState';
import { useIsPlayerStarted } from '@/scenarios/legacy-player/hooks/useIsPlayerStarted';

import { useControlsVisibility } from './hooks/use-controls-visibility';
import styles from './styles.module.css';

interface ControlRenderParams {
    isControlsVisible: boolean;
    setIsHidingControlsBlocked: (isHidingControlsBlocked: boolean) => void;
}

type ControlRenderProp = (params: ControlRenderParams) => ReactNode;

interface Props {
    isAlwaysVisible?: boolean;
    onClick?: () => void;
    onDoubleClick?: () => void;
    renderTopControls?: ControlRenderProp;
    renderBottomControls?: ControlRenderProp;
    renderControls?: ControlRenderProp;
    renderPopups?: ControlRenderProp;
    className?: string;
}

export const FullSizePlayerLayout: FC<Props> = ({
    isAlwaysVisible,
    className,
    onClick,
    onDoubleClick,
    renderTopControls,
    renderBottomControls,
    renderControls,
    renderPopups,
}) => {
    const { isControlsVisible, initiateControlsVisibility, setIsHidingControlsBlocked } =
        useControlsVisibility(isAlwaysVisible);
    const { playingState } = usePlayingState();
    const isPlayerStarted = useIsPlayerStarted();
    const onEmptySpaceClick = useDoubleClick({
        onClick,
        onDoubleClick,
    });

    useLayoutEffect(() => {
        const oldBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = oldBodyOverflow;
        };
    }, []);

    return (
        <div
            onMouseMove={initiateControlsVisibility}
            className={classnames(styles.root, className, {
                [styles.withControls]: isControlsVisible && renderControls,
            })}
        >
            {renderTopControls && (
                <div className={styles.topControlsLayer}>
                    <TopTransition isVisible={isControlsVisible}>
                        {renderTopControls({ setIsHidingControlsBlocked, isControlsVisible })}
                    </TopTransition>
                </div>
            )}
            {renderBottomControls && (
                <div className={styles.bottomControlsLayer}>
                    <BottomTransition isVisible={isControlsVisible && isPlayerStarted}>
                        {renderBottomControls({
                            setIsHidingControlsBlocked,
                            isControlsVisible,
                        })}
                    </BottomTransition>
                </div>
            )}
            <div
                className={classnames(styles.controlsLayer, {
                    [styles.withoutEvents]: !renderControls,
                })}
            >
                {renderControls &&
                    renderControls({ isControlsVisible, setIsHidingControlsBlocked })}
                {playingState === 'buffering' && (
                    <Spinner size={SpinnerSize.BIG} className={styles.spinner} />
                )}
                <div
                    role="presentation"
                    className={classnames(styles.interactiveBlock, {
                        [styles.withControls]: isControlsVisible,
                    })}
                    onClick={onEmptySpaceClick}
                />
            </div>
            <div className={classnames(styles.popupsLayer)}>
                {renderPopups && renderPopups({ isControlsVisible, setIsHidingControlsBlocked })}
            </div>
        </div>
    );
};
